// Account Structure → "Overall Organization" judgment row.
// Layout: the LLM verdict block at top (the headline output), followed by a collapsible
// "Campaigns Claude saw" section showing the underlying data the verdict was grounded in.

import type { EvidenceProps } from './registry';

interface CampaignSummary {
  id?: string;
  name?: string;
  channelType?: string;
  channelSubType?: string;
}

interface AccountStructureEvidenceShape {
  accountName: string;
  cid: string;
  campaignCount: number;
  campaigns: CampaignSummary[];
}

// Channel-type labels — keys cover both Google's UPPER_SNAKE values and Microsoft's PascalCase
// values, so the same renderer handles both platforms. Unknown values fall through to raw.
const CHANNEL_LABEL: Record<string, string> = {
  // Google
  SEARCH: 'Search',
  SHOPPING: 'Shopping',
  PERFORMANCE_MAX: 'Performance Max',
  VIDEO: 'Video',
  DISPLAY: 'Display',
  DEMAND_GEN: 'Demand Gen',
  DISCOVERY: 'Discovery',
  HOTEL: 'Hotel',
  LOCAL: 'Local',
  LOCAL_SERVICES: 'Local Services',
  MULTI_CHANNEL: 'Multi-channel',
  SMART: 'Smart',
  TRAVEL: 'Travel',
  // Microsoft
  Search: 'Search',
  Shopping: 'Shopping',
  PerformanceMax: 'Performance Max',
  DynamicSearchAds: 'Dynamic Search Ads',
  Audience: 'Audience'
};

function channelLabel(t: string | undefined): string {
  if (!t) return '—';
  return CHANNEL_LABEL[t] ?? t;
}

export const AccountStructureEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as AccountStructureEvidenceShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Campaigns Claude saw
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.campaignCount} enabled
          </span>
        </h4>
        <table className='evidence__table evidence__table--account-structure'>
          <thead>
            <tr>
              <th>Campaign name</th>
              <th>Channel</th>
              <th className='evidence__th--right'>Campaign ID</th>
            </tr>
          </thead>
          <tbody>
            {e.campaigns.map((c, i) => (
              <tr key={c.id ?? `${c.name}-${i}`}>
                <td>{c.name ?? '(unnamed)'}</td>
                <td>
                  {channelLabel(c.channelType)}
                  {c.channelSubType && c.channelSubType !== 'UNSPECIFIED'
                    ? ` / ${c.channelSubType}`
                    : ''}
                </td>
                <td className='evidence__td--right'>{c.id ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};
